import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@v2/components/ui/button";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { PLAYER_ROLES, playerRoleLabel } from "../../lib/labels";
import { currentIntlLocale } from "../../lib/i18n";

type Mode = "closed" | "existing" | "new";

export type EaMemberOption = {
  ea_persona: string;
  ea_name: string | null;
  pro_name: string | null;
  alreadyLinked: boolean;
};

export function PendingRequestRow({
  request,
  players,
  eaMembers,
  onApproveExisting,
  onApproveCreate,
  onReject,
  isWorking,
}: {
  request: {
    id: string;
    email: string | null;
    user_id: string;
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    note: string | null;
    requested_at: string;
  };
  players: Array<{ id: string; display_name: string; last_name: string | null; ea_sports_id: string | null; user_id: string | null }>;
  eaMembers: EaMemberOption[];
  onApproveExisting: (playerId: string | null) => void;
  onApproveCreate: (payload: {
    first_name: string;
    last_name: string;
    role_code: string | null;
    shirt_number: number | null;
    ea_account_handle: string | null;
  }) => void;
  onReject: () => void;
  isWorking: boolean;
}) {
  const { t } = useTranslation("requests");
  const [mode, setMode] = useState<Mode>("closed");
  const [playerId, setPlayerId] = useState<string>("");
  const [firstName, setFirstName] = useState(request.first_name || "");
  const [lastName, setLastName] = useState(request.last_name || "");
  const [roleCode, setRoleCode] = useState("");
  const [shirtNumber, setShirtNumber] = useState("");
  const [eaHandle, setEaHandle] = useState<string>("");

  const submitCreate = () => {
    if (!firstName.trim() || !lastName.trim()) return;
    onApproveCreate({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      role_code: roleCode.trim() || null,
      shirt_number: shirtNumber.trim() ? Number(shirtNumber) : null,
      ea_account_handle: eaHandle.trim() || null,
    });
  };

  return (
    <div className="border rounded-md p-3 bg-card space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">
            {request.full_name && request.full_name !== request.email
              ? request.full_name
              : (request.email || request.user_id.slice(0, 8) + "…")}
          </p>
          {request.full_name && request.full_name !== request.email && request.email && (
            <p className="text-xs text-muted-foreground truncate">{request.email}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {t("pendingRow.requestedOn", { date: new Date(request.requested_at).toLocaleDateString(currentIntlLocale()) })}
          </p>
          {request.note && <p className="text-xs italic mt-1">"{request.note}"</p>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button
            size="sm"
            variant={mode === "existing" ? "default" : "outline"}
            onClick={() => setMode((m) => (m === "existing" ? "closed" : "existing"))}
          >
            {t("pendingRow.approveExisting")}
          </Button>
          <Button
            size="sm"
            variant={mode === "new" ? "default" : "outline"}
            onClick={() => setMode((m) => (m === "new" ? "closed" : "new"))}
          >
            {t("pendingRow.createNew")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onApproveExisting(null)}
            disabled={isWorking}
            title={t("pendingRow.approveOnlyTitle")}
          >
            {t("pendingRow.approveOnly")}
          </Button>
          <Button size="sm" variant="ghost" className="text-rose-600" onClick={onReject} disabled={isWorking}>
            {t("pendingRow.reject")}
          </Button>
        </div>
      </div>

      {mode === "existing" && (
        <div className="space-y-1.5 pt-2 border-t">
          <div className="flex flex-wrap gap-1.5">
            <Select value={playerId} onValueChange={setPlayerId}>
              <SelectTrigger className="h-8 flex-1 min-w-[200px]">
                <SelectValue placeholder={t("pendingRow.selectPlayer")} />
              </SelectTrigger>
              <SelectContent>
                {players.map((p) => (
                  <SelectItem key={p.id} value={p.id} disabled={!!p.user_id}>
                    {p.display_name}
                    {p.ea_sports_id ? ` · EA ${p.ea_sports_id}` : ""}
                    {p.user_id ? t("pendingRow.alreadyLinked") : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              className="h-8"
              disabled={!playerId || isWorking}
              onClick={() => onApproveExisting(playerId)}
            >
              {t("pendingRow.confirm")}
            </Button>
          </div>
          {(() => {
            const sel = players.find((p) => p.id === playerId);
            const isStub = !!sel && (!sel.last_name || sel.last_name.trim() === "");
            if (!isStub) return null;
            const newName = [request.first_name, request.last_name].filter(Boolean).join(" ").trim()
              || request.full_name
              || null;
            return (
              <p className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-md px-2 py-1">
                {t("pendingRow.stubNote")}
                {newName
                  ? t("pendingRow.stubWithName", { name: newName })
                  : t("pendingRow.stubFromUser")}
              </p>
            );
          })()}
        </div>
      )}

      {mode === "new" && (
        <div className="pt-2 border-t space-y-2">
          <p className="text-xs text-muted-foreground">
            {t("pendingRow.newPlayerIntro")}
            {request.full_name ? t("pendingRow.newPlayerPrefilled") : ""}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor={`req-${request.id}-fn`} className="text-xs">{t("pendingRow.firstName")}</Label>
              <Input
                id={`req-${request.id}-fn`}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t("pendingRow.firstNamePh")}
                className="h-8 text-sm"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`req-${request.id}-ln`} className="text-xs">{t("pendingRow.lastName")}</Label>
              <Input
                id={`req-${request.id}-ln`}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t("pendingRow.lastNamePh")}
                className="h-8 text-sm"
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("pendingRow.role")}</Label>
              <Select value={roleCode || "__none"} onValueChange={(v) => setRoleCode(v === "__none" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">{t("pendingRow.roleNone")}</SelectItem>
                  {PLAYER_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      <span className="font-mono mr-2">{r}</span>
                      <span className="text-muted-foreground">{playerRoleLabel(r)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`req-${request.id}-sn`} className="text-xs">{t("pendingRow.number")}</Label>
              <Input
                id={`req-${request.id}-sn`}
                type="number"
                min={1}
                max={99}
                value={shirtNumber}
                onChange={(e) => setShirtNumber(e.target.value)}
                placeholder={t("pendingRow.numberPh")}
                className="h-8 text-sm"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("pendingRow.eaLink")}</Label>
            {eaMembers.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                {t("pendingRow.eaLinkEmpty")}
              </p>
            ) : (
              <Select value={eaHandle} onValueChange={setEaHandle}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder={t("pendingRow.eaLinkPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t("pendingRow.eaLinkNone")}</SelectItem>
                  {eaMembers.map((m) => {
                    const proLabel =
                      m.pro_name && m.pro_name !== m.ea_persona && m.pro_name !== m.ea_name
                        ? ` · Pro: ${m.pro_name}`
                        : "";
                    const nameLabel =
                      m.ea_name && m.ea_name !== m.ea_persona ? ` · ${m.ea_name}` : "";
                    return (
                      <SelectItem key={m.ea_persona} value={m.ea_persona} disabled={m.alreadyLinked}>
                        {m.ea_persona}
                        {nameLabel}
                        {proLabel}
                        {m.alreadyLinked ? t("pendingRow.eaAlreadyLinked") : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-8"
              disabled={isWorking || !firstName.trim() || !lastName.trim()}
              onClick={submitCreate}
            >
              {isWorking ? t("pendingRow.creating") : t("pendingRow.createAndApprove")}
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setMode("closed")}>
              {t("pendingRow.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
